import { JwtTokenFullData } from 'multirpc-sdk';
import { createSelector } from 'reselect';
import { t } from '@ankr.com/common';

import { MultiService } from 'modules/api/MultiService';
import { NotificationActions } from 'domains/notifications/store/NotificationActions';
import { TFAEndpointMeta, TFAQueryFnParams } from 'modules/tfa/types';
import { createQueryFnWithErrorHandler } from 'store/utils/createQueryFnWithErrorHandler';
import { createQuerySelectors } from 'store/utils/createQuerySelectors';
import { resolveJWTWithApiKey } from 'modules/common/utils/resolveJWTWithApiKey';
import { isTFAError } from 'modules/tfa/utils/isTFAError';
import { signOut } from 'modules/auth/actions/signOut';
import { web3Api } from 'store/queries';
import { ACTION_CACHE_LONG } from 'modules/common/constants/const';

import { isEmptyEthAddressAuthError } from './utils/isEmptyEthAddressAuthError';

const { showNotification } = NotificationActions;

export interface IFetchStandardAccountJWTProps
  extends TFAQueryFnParams<unknown> {}

// The endpoint name is listed in endpointsSerializedByParams constant
// in packages/protocol/src/store/queries/index.ts file.
// If the name has changed it should be reflected there as well.
export const {
  endpoints: { fetchStandardAccountJWT },
  useFetchStandardAccountJWTQuery,
  useLazyFetchStandardAccountJWTQuery,
} = web3Api.injectEndpoints({
  endpoints: build => ({
    fetchStandardAccountJWT: build.query<
      JwtTokenFullData,
      IFetchStandardAccountJWTProps
    >({
      keepUnusedDataFor: ACTION_CACHE_LONG, // using long cache in order to save tokens in store. refetch causes pages reload
      queryFn: createQueryFnWithErrorHandler({
        queryFn: async ({ totp }) => {
          const api = MultiService.getService().getAccountingGateway();

          const jwt = await api.getOrCreateInstantJwtWithApiKey(totp);

          const data = await resolveJWTWithApiKey(jwt);

          return { data };
        },
        errorHandler: (error, _args, { dispatch }) => {
          if (isEmptyEthAddressAuthError(error)) {
            dispatch(
              showNotification({
                autoHideDuration: null,
                message: t('error.empty-eth'),
                severity: 'error',
              }),
            );

            dispatch(signOut.initiate());
          }

          const meta: TFAEndpointMeta = {
            shouldSignOutOnTFAError: true,
          };

          return { error, meta };
        },
      }),
    }),
  }),
  overrideExisting: true,
});

const fetchStandardAccountJWTFallback: JwtTokenFullData = {};

export const {
  selectDataWithFallbackCachedByParams: selectStandardAccountJWT,
  selectStateCachedByParams: selectStandardAccountJWTState,
} = createQuerySelectors({
  endpoint: fetchStandardAccountJWT,
  fallback: fetchStandardAccountJWTFallback,
});

export const selectStandardAccountJWTLoading = createSelector(
  selectStandardAccountJWTState,
  ({ error, isLoading }) => isLoading || isTFAError(error),
);
